/* The frame every workspace screen sits in — STORY-039.
 *
 * The story exists because navigation was never specified: the first prototype
 * invented a four-item tab bar with no decision behind it, and that is how the
 * Calendar and the Inventory came to be unreachable. So the destination list is
 * declared ONCE, here, and both surfaces render the same eight. Two different
 * sets would be two products.
 *
 * Calendar is home. It already aggregates Events and Tasks due (STORY-014), so
 * it is the only screen that answers "what is going on" in one view.
 *
 * No destination is ever hidden by a plan. `pricing.md` gates scale, not
 * features, and a limit is met at the action that consumes it — hiding a
 * destination is how people conclude a feature does not exist.
 *
 * **One state machine, two presentations.** The drawer is open or closed at
 * every width; CSS decides whether that reads as a slide-over with a scrim or
 * as a static sidebar where the control is hidden. Branching on a media query
 * in JavaScript would mean two implementations of one behaviour, and they drift.
 */

import type { MessageKey } from '@eventuley/i18n';
/* Phosphor, one weight — Regular — per STORY-039. `design/components/icon`
 * inlines the same glyphs as an SVG sprite because the design library has no
 * build step, so a mockup and the built screen show the same shapes. */
import {
  CalendarBlankIcon,
  CheckSquareIcon,
  ConfettiIcon,
  CurrencyCircleDollarIcon,
  GearIcon,
  ListIcon,
  MapPinIcon,
  PackageIcon,
  UsersIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CURRENT_USER_KEY, useCurrentUser, useSignOut } from '../features/auth/index.js';
import { useOrganisation } from '../features/organisations/index.js';
import { OverLimitBanner, useUsage } from '../features/plans/index.js';
import { ApiError } from '../shared/api/client.js';
import { useTranslate } from '../shared/i18n/index.js';
import { LanguagePicker, WorkspaceLockProvider } from '../shared/ui/index.js';

/** The eight, in order. Calendar first because it is home. */
const DESTINATIONS = [
  { to: '/orgs/$organisationId/calendar', key: 'nav.calendar', Icon: CalendarBlankIcon },
  { to: '/orgs/$organisationId/events', key: 'nav.events', Icon: ConfettiIcon },
  { to: '/orgs/$organisationId/venues', key: 'nav.venues', Icon: MapPinIcon },
  { to: '/orgs/$organisationId/people', key: 'nav.people', Icon: UsersIcon },
  { to: '/orgs/$organisationId/tasks', key: 'nav.tasks', Icon: CheckSquareIcon },
  { to: '/orgs/$organisationId/money', key: 'nav.money', Icon: CurrencyCircleDollarIcon },
  { to: '/orgs/$organisationId/inventory', key: 'nav.inventory', Icon: PackageIcon },
  { to: '/orgs/$organisationId/settings', key: 'nav.settings', Icon: GearIcon },
] as const satisfies readonly { to: string; key: MessageKey; Icon: unknown }[];

const FOCUSABLE = 'a[href], button:not([disabled])';

export function WorkspaceShell() {
  const t = useTranslate();
  const navigate = useNavigate();
  /* `strict: false` because the shell is a LAYOUT: it renders under whichever
   * destination is active, so the param it needs is carried by a child route
   * rather than by the route this component was declared on. Pinning `from` to
   * the layout's own path throws the moment a destination is mounted. */
  const { organisationId = '' } = useParams({ strict: false });
  /* Cached by TanStack Query and shared with the plans page, so the banner
   * costs no extra request on most navigations. */
  const { data: usage } = useUsage(organisationId);

  /* Read-only while any meter is over — STORY-045. Derived from the same
   * numbers the banner renders, so the controls and the explanation above them
   * can never disagree. */
  const locked = usage
    ? usage.overBy.activeEvents > 0 || usage.overBy.seats > 0 || usage.overBy.contacts > 0
    : false;
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const { data: user } = useCurrentUser();
  const { data: organisation, error } = useOrganisation(organisationId);
  const signOut = useSignOut();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    /* Focus goes back to the control that opened it. This is the half that gets
     * forgotten, and without it a keyboard user is dropped at the top of the
     * document with no idea what just happened. */
    openerRef.current?.focus();
  }, []);

  /* Opening moves focus into the drawer. A modal that leaves focus outside
   * itself lets the next Tab walk the page behind the scrim, which is the thing
   * a trap exists to prevent. */
  useEffect(() => {
    if (!open) return;
    const first = navRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
  }, [open]);

  /* Navigating closes it. On mobile the destination is now behind the drawer;
   * on desktop the state is invisible and closing it is harmless. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger — the effect exists to react to a route change, and `open` is deliberately not a dependency because re-running on open would close it immediately.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  /* A workspace the reader holds no Membership in answers 404, exactly as one
   * that does not exist does. Sending them to the chooser rather than saying
   * "you do not have access" is what keeps that refusal honest — a client that
   * names the reason gives away the answer the server withheld.
   *
   * This is also what a Membership REVOKED mid-session looks like — STORY-009.
   * The next request is refused and they land back on the chooser, with their
   * sessions for every other workspace untouched. */
  const refused = error instanceof ApiError && error.status === 404;
  useEffect(() => {
    if (!refused) return;
    /* The cached User still lists the Membership that just went away, and the
     * chooser is about to render from it. Re-reading is what stops it offering
     * a way back into a workspace they are no longer in. */
    void queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY });
    void navigate({ to: '/next', replace: true });
  }, [refused, navigate, queryClient]);

  /* Signed out, or the session expired while they were working. Back to
   * sign-in, carrying where they were so it resolves afterwards rather than
   * dropping them on a chooser having forgotten what they clicked. */
  const unauthenticated = error instanceof ApiError && error.status === 401;
  /* Once. The effect watches `pathname` so it carries the right target, which
   * also means it re-fires as the location changes — and the second run would
   * overwrite the destination with wherever the first run just went. */
  const bounced = useRef(false);
  useEffect(() => {
    if (!unauthenticated || bounced.current) return;
    bounced.current = true;
    void navigate({ to: '/sign-in', search: { redirect: pathname }, replace: true });
  }, [unauthenticated, navigate, pathname]);

  /* Announced, not merely rendered. A context switch nobody is told about is
   * disorienting, and for a screen-reader user the topbar changing is
   * invisible — `design.md` and STORY-009's accessibility criterion.
   *
   * Set from an effect so the region is in the document before it has content:
   * a live region announces UPDATES, so text present at first paint is text
   * nobody hears. */
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    if (!organisation?.name) return;
    setAnnouncement(t('workspace.switch.entered', { name: organisation.name }));
  }, [organisation?.name, t]);

  function onKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (!open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    const items = [...(navRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;

    /* The trap. Wrapping at both ends rather than only the last, because
     * Shift+Tab off the front is the same escape by a different key. */
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Read from the Membership rather than from the summary, so the line says
   * what this User is in THIS workspace even before the summary lands. */
  const membership = user?.memberships.find((held) => held.organisationId === organisationId);
  const roleKey: MessageKey = membership?.role === 'owner' ? 'role.owner' : 'role.member';

  /* An explicit map, never `t('plans.' + code)`. A constructed key defeats
   * extraction and unused-key detection — `i18n.md`. Falls back to the Free
   * label only because the workspace's plan is `free` by default and the CHECK
   * constraint bounds it to these four. */
  const PLAN_KEYS: Record<string, MessageKey> = {
    free: 'plans.free',
    pro: 'plans.pro',
    business: 'plans.business',
    enterprise: 'plans.enterprise',
  };
  const planKey: MessageKey = PLAN_KEYS[organisation?.plan ?? 'free'] ?? 'plans.free';

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the handler is the drawer's focus trap and its Escape, which belong to the container the trap is over. Nothing here is made clickable.
    <div className="app" data-drawer={open ? 'open' : 'closed'} onKeyDown={onKeyDown}>
      <header className="topbar">
        <button
          ref={openerRef}
          className="icon-button topbar__menu"
          type="button"
          aria-expanded={open}
          aria-controls="workspace-nav"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
        >
          <ListIcon className="icon" aria-hidden="true" />
          {/* The name says what activating it WILL do. It read "Open the menu"
              in both states, so a screen-reader user heard "open" on the
              control that was about to close. */}
          <span className="sr-only">{open ? t('nav.close') : t('nav.open')}</span>
        </button>
        <span className="topbar__org">{organisation?.name ?? ''}</span>

        {/* Language, in the bar — STORY-043. STORY-042 built the control and
            connected the resolution chain, and then left the only in-workspace
            way to reach it behind the drawer, on Account: three steps of
            reading, in the language the reader is asking to change BECAUSE
            they cannot read it. The design library has carried
            `.topbar__lang` since the shell was promoted; the shell simply
            never rendered it.

            One control, not a second one: the same component the Account
            screen and every auth screen render, so the value, the write, and
            the failure cannot differ by where somebody changed their mind. */}
        <LanguagePicker variant="bar" />
      </header>

      {/* Which workspace you are now in, said out loud — STORY-009. The topbar
          above says it visually; this is the half a screen reader gets. */}
      <p className="sr-only" role="status">
        {announcement}
      </p>

      <div className="shell">
        {/* The scrim is presentational: it is `aria-hidden` and the trap above
            is what actually confines focus, so a screen-reader user is not told
            about a decorative overlay. */}
        {open ? (
          <div className="nav__scrim" aria-hidden="true" role="presentation" onClick={close} />
        ) : null}

        <nav className="nav" id="workspace-nav" aria-label={t('nav.label')} ref={navRef}>
          {/* A visible way out. Escape and a tap on the scrim are the only other
              dismissals, and neither is discoverable on a touch screen — the
              stylesheet has revealed `.nav__close` below 48rem since the shell
              was promoted, and nothing rendered it. */}
          <button className="icon-button nav__close" type="button" onClick={close}>
            <XIcon className="icon" aria-hidden="true" />
            <span className="sr-only">{t('nav.close')}</span>
          </button>

          <div className="nav__dests">
            {DESTINATIONS.map((destination) => (
              <Link
                key={destination.to}
                to={destination.to}
                params={{ organisationId }}
                /* `aria-current="page"`, not a class alone: a destination marked
                 * only by colour is not marked at all for a screen-reader user,
                 * and `design.md` forbids colour carrying meaning by itself. */
                activeProps={{ 'aria-current': 'page' }}
              >
                {/* Decoration. The label names the link; an icon announced
                    alongside it would say everything twice, and an icon
                    WITHOUT a label is a guess — STORY-039. */}
                <destination.Icon className="icon" aria-hidden="true" />
                {t(destination.key)}
              </Link>
            ))}
          </div>

          {/* At the foot and last in the tab order: least used, and most costly
              to activate by accident. The Role sits beside the name because it
              is per Membership — you can be an Owner here and a Member in the
              next workspace, and this is the only line that says which. */}
          <div className="nav__account">
            <p className="nav__whoami">
              <span className="nav__name">{user?.displayName ?? ''}</span>
              <span className="nav__email">{user?.email ?? ''}</span>
              <span className="nav__role">{t(roleKey)}</span>
            </p>

            {/* The plan, where somebody can actually see it.
                It was readable only by navigating to `/plans`, which nothing in
                the app linked to — so the one fact that decides what every
                screen will refuse was invisible everywhere it mattered. Beside
                the Role because both are properties of THIS workspace: you can
                be an Owner on Pro here and a Member on Free next door. */}
            <Link
              className="nav__plan"
              to="/orgs/$organisationId/billing"
              params={{ organisationId }}
            >
              <span className="badge">{t(planKey)}</span>
              <span className="nav__plan-action">{t('nav.billing')}</span>
            </Link>

            <Link className="nav__link" to="/account">
              {t('nav.account')}
            </Link>
            <Link className="nav__link" to="/next">
              {t('nav.switch_workspace')}
            </Link>
            <button
              className="nav__link nav__link--button"
              type="button"
              onClick={() => signOut.mutate()}
            >
              {t('nav.sign_out')}
            </button>
          </div>
        </nav>

        <main className="main">
          <div className="main__inner">
            {/* Above the page, on every workspace screen — STORY-045. A
                workspace over its plan needs to know on whichever screen it is
                looking at, not only on the one that refused it. */}
            <OverLimitBanner usage={usage} />
            {/* Disables every Save below it while the workspace is over its
                plan, so a control that cannot work says so BEFORE it is
                pressed. Reductions are click handlers and are untouched. */}
            <WorkspaceLockProvider locked={locked}>
              <Outlet />
            </WorkspaceLockProvider>
          </div>
        </main>
      </div>
    </div>
  );
}
